import { type FC, useState } from "react";
import type { ProfileChangeDto } from "shared";
import { Button } from "../components/Button";
import { Card } from "../components/Card";
import { useProfileHistory } from "../settings/useIntegrations";

function formatWhen(iso: string) {
	try {
		return new Date(iso).toLocaleString(undefined, {
			dateStyle: "medium",
			timeStyle: "short",
		});
	} catch {
		return iso;
	}
}

function summarizeFields(fields: Record<string, unknown>): string {
	const source = typeof fields.source === "string" ? fields.source : null;
	const keys = Object.keys(fields).filter(
		(k) => k !== "source" && k !== "fields",
	);
	if (source === "import-from-google") return "Imported from Google";
	if (source === "publish-everywhere") return "Published everywhere";
	if (source === "sync-from-google-read") return "Synced from Google read";
	if (keys.length === 0 && fields.fields) return "Profile fields updated";
	if (keys.length === 0) return "Profile updated";
	return keys
		.map((k) => k.replace(/_/g, " "))
		.slice(0, 6)
		.join(", ");
}

function whoLabel(change: ProfileChangeDto) {
	if (change.userName) return change.userName;
	if (change.userEmail) return change.userEmail;
	return "System";
}

function platformsLabel(platforms: string[]) {
	if (platforms.length === 0) return "no push";
	return platforms.join(", ");
}

export const ProfileHistory: FC<{ locationId?: string }> = ({ locationId }) => {
	const [open, setOpen] = useState(false);
	const history = useProfileHistory(open, locationId);

	return (
		<Card style={{ marginBottom: 12 }}>
			<Card.Header
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 8,
				}}
			>
				<span>Edit history</span>
				<Button
					variant="ghost"
					size="sm"
					icon={open ? "keyboard_arrow_up" : "keyboard_arrow_down"}
					onClick={() => setOpen((v) => !v)}
				>
					{open ? "Hide" : "Show"}
				</Button>
			</Card.Header>
			{open && (
				<Card.Body>
					{history.isLoading && (
						<p className="text-muted mb-0" style={{ fontSize: "0.875rem" }}>
							Loading history…
						</p>
					)}
					{history.isError && (
						<p className="text-danger mb-0" style={{ fontSize: "0.875rem" }}>
							Failed to load edit history.
						</p>
					)}
					{history.data && history.data.changes.length === 0 && (
						<p className="text-muted mb-0" style={{ fontSize: "0.875rem" }}>
							No profile changes recorded yet.
						</p>
					)}
					{history.data && history.data.changes.length > 0 && (
						<ul
							style={{
								listStyle: "none",
								padding: 0,
								margin: 0,
								display: "flex",
								flexDirection: "column",
								gap: 10,
							}}
						>
							{history.data.changes.map((change) => (
								<li
									key={change.id}
									style={{
										borderBottom: "1px solid var(--bs-border-color, #e9ecef)",
										paddingBottom: 8,
									}}
								>
									<div
										style={{
											display: "flex",
											justifyContent: "space-between",
											gap: 8,
											flexWrap: "wrap",
											fontSize: "0.875rem",
										}}
									>
										<strong>{whoLabel(change)}</strong>
										<span className="text-muted">
											{formatWhen(change.createdAt)}
										</span>
									</div>
									<div style={{ fontSize: "0.8125rem", marginTop: 2 }}>
										{summarizeFields(change.changedFields)}
									</div>
									<div
										className="text-muted"
										style={{ fontSize: "0.75rem", marginTop: 2 }}
									>
										Pushed: {platformsLabel(change.pushedPlatforms)}
									</div>
								</li>
							))}
						</ul>
					)}
				</Card.Body>
			)}
		</Card>
	);
};
